import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import Mock1 from '../../../public/assets/images/nawy-now/step-1.webp';
import Mock2 from '../../../public/assets/images/nawy-now/step-2.webp';
import Mock3 from '../../../public/assets/images/nawy-now/step-3.webp';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

const SCROLL_THRESHOLD = 60;

const StepsSection = ({
  propertiesRef,
  formRef
}: {
  propertiesRef: React.RefObject<HTMLDivElement>;
  formRef: React.RefObject<HTMLDivElement>;
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [delta, setDelta] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const locked = useRef(false);
  const { t } = useTranslation('nawy-now');
  const router = useRouter();
  const steps = [
    {
      title: t('title_step_1'),
      description: 'description_step_1'
    },
    {
      title: (
        <>
          {t('contact')}{' '}
          <span className="orange">
            {t('nawy')} {t('now')}
          </span>{' '}
          {t('consultant')}
        </>
      ),
      description: 'description_step_2'
    },
    {
      title: (
        <>
          {t('move')}{' '}
          {router.locale == 'en' && <span className="orange">{t('now')}</span>}
        </>
      ),
      description: 'description_step_3'
    }
  ];
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) return;

      const rect = containerRef.current.getBoundingClientRect();
      const triggerPoint = window.innerHeight * 0.25;
      if (rect.top > triggerPoint) return;

      if (activeStep === 0 && e.deltaY < 0) return;
      if (activeStep === steps.length - 1 && e.deltaY > 0) return;

      if (locked.current) {
        e.preventDefault();
        return;
      }
      let newDelta = delta;
      if (e.deltaY < 0) {
        newDelta -= Math.abs(e.deltaY);
        setDelta(newDelta);
        if (Math.abs(newDelta) >= SCROLL_THRESHOLD) {
          locked.current = true;
          prevStep();
          setDelta(0);
          e.preventDefault();
          setTimeout(() => {
            locked.current = false;
          }, 1000);
          return;
        }
      } else if (e.deltaY > 0) {
        newDelta += Math.abs(e.deltaY);
        setDelta(newDelta);
        if (newDelta >= SCROLL_THRESHOLD) {
          locked.current = true;
          nextStep();
          setDelta(0);
          e.preventDefault();
          setTimeout(() => {
            locked.current = false;
          }, 1000);
          return;
        }
      }
      e.preventDefault();
    };

    const node = containerRef.current;
    if (node) node.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      if (node) node.removeEventListener('wheel', handleWheel);
    };
  }, [delta, activeStep]);

  useEffect(() => {
    setDelta(0);
  }, [activeStep]);
  useEffect(() => {
    imageRefs.current.forEach((ref, idx) => {
      if (!ref) return;
      if (idx === activeStep) {
        if (ref.style.opacity !== '1') {
          gsap.to(ref, {
            opacity: 1,
            zIndex: 2,
            duration: 0.6,
            ease: 'power2.out',
            pointerEvents: 'auto'
          });
        } else {
          ref.style.zIndex = '2';
          ref.style.pointerEvents = 'auto';
        }
      } else {
        if (ref.style.opacity !== '0') {
          gsap.to(ref, {
            opacity: 0,
            zIndex: 1,
            duration: 0.6,
            ease: 'power2.out',
            pointerEvents: 'none'
          });
        } else {
          ref.style.zIndex = '1';
          ref.style.pointerEvents = 'none';
        }
      }
    });
  }, [activeStep]);

  const prevStep = () => {
    setActiveStep(prev => (prev <= 0 ? steps.length - 1 : prev - 1));
  };

  const nextStep = () => {
    setActiveStep(prev => (prev >= steps.length - 1 ? 0 : prev + 1));
  };

  const handleClick = (ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return;
    const offset = 100;
    const elementPosition = ref?.current?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  const handleTitleClick = (idx: number) => {
    setActiveStep(idx);
    // const ref = idx === 0 ? propertiesRef : formRef;
    // if (ref.current) {
    //   const offset = 100;
    //   const elementPosition = ref.current.getBoundingClientRect().top;
    //   const offsetPosition = elementPosition + window.scrollY - offset;

    //   window.scrollTo({
    //     top: offsetPosition,
    //     behavior: 'smooth'
    //   });
    // }
  };

  return (
    <Container ref={containerRef} tabIndex={0}>
      <div className="container-fluid inner-content">
        <div className="content-details">
          <h3 className="steps-title">
            <span className="orange">
              {t('nawy')} {t('now')}
            </span>{' '}
            &nbsp;
            {t('your_fastest_path')}
          </h3>
          <div className="steps-details">
            <div className="steps-slider">
              <div
                className="steps-slider-active"
                style={{
                  top: `calc(${activeStep} * (100% / ${steps.length}))`,
                  height: `calc(100% / ${steps.length})`
                }}
              ></div>
            </div>
            <div className="steps-container">
              {steps.map((step, idx) => (
                <div
                  className={
                    'step-item' + (idx === activeStep ? ' is-active' : '')
                  }
                  key={idx}
                >
                  <span
                    className="step-title"
                    onClick={() => handleTitleClick(idx)}
                  >
                    {step.title}
                  </span>
                  <span className="step-description">
                    {t(step.description)}
                  </span>
                  {idx == 0 ? (
                    <button
                      className="step-action"
                      onClick={() => handleClick(propertiesRef)}
                    >
                      <span>{t('go_to_properties')}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="21"
                        viewBox="0 0 20 21"
                        fill="none"
                      >
                        <path
                          d="M15.0615 12.8359L10.0032 17.8943L4.94486 12.8359"
                          stroke="#FF5E00"
                          stroke-width="1.5"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M10 3.73438L10 17.7594"
                          stroke="#FF5E00"
                          stroke-width="1.5"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  ) : idx == 1 ? (
                    <button
                      className="step-action"
                      onClick={() => handleClick(formRef)}
                    >
                      <span>{t('fill_form')}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="21"
                        viewBox="0 0 20 21"
                        fill="none"
                      >
                        <path
                          d="M15.0615 12.8359L10.0032 17.8943L4.94486 12.8359"
                          stroke="#FF5E00"
                          stroke-width="1.5"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M10 3.73438L10 17.7594"
                          stroke="#FF5E00"
                          stroke-width="1.5"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  ) : (
                    <></>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="image-section">
          <div className="image-container">
            {[Mock1, Mock2, Mock3].map((mock, idx) => (
              <div
                key={idx}
                ref={el => {
                  imageRefs.current[idx] = el;
                }}
                style={{
                  position: 'absolute',
                  inset: 0
                }}
              >
                <Image
                  src={mock}
                  alt={`Steps Section ${idx + 1}`}
                  fill
                  style={{ objectFit: 'cover' }}
                  className="steps-image"
                  sizes="(max-width: 600px) 100vw, 316px"
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div`
  width: 100vw;
  background: #f8f8f8;
  outline: none;

  .inner-content {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 140px;
    .content-details {
      display: flex;
      flex-direction: column;
      /* flex: 1; */
      padding: 40px 0;
      gap: 32px;
      h3 {
        color: #2c3138;
        font-family: materialBold;
        font-size: 34px;
        line-height: 50px;
        text-transform: uppercase;
        max-width: 475px;
      }

      .steps-details {
        display: flex;
        flex-direction: row;
        gap: 90px;
        align-items: flex-start;

        .steps-slider {
          position: relative;
          width: 5px;
          background: #e5e5e5;
          border-radius: 3px;
          height: 100%;
          min-height: 180px;
          transition: background 0.5s;

          .steps-slider-active {
            position: absolute;
            left: 0;
            width: 100%;
            background: #111;
            border-radius: 3px;
            transition:
              top 0.3s,
              height 0.3s;
          }
        }
        .steps-container {
          display: flex;
          flex-direction: column;
          gap: 32px;

          .step-item {
            display: flex;
            flex-direction: column;
            gap: 12px;

            transition:
              opacity 0.3s,
              filter 0.3s;
            .step-title {
              color: #2c3138 !important;
              font-family: materialSemiBold;
              font-size: 24px;
              line-height: 140%;
              text-transform: capitalize;
              opacity: 0.2;
              cursor: pointer;
              span {
                color: #2c3138;
              }
            }
            .step-description {
              color: #2c3138;
              font-family: materialRegular;
              font-size: 18px;
              line-height: 150%;
              max-width: 475px;
              overflow: hidden;
              opacity: 0;
              height: 0;
              transition:
                opacity 0.3s,
                height 0.3s;
            }
            .step-action {
              display: flex;
              align-items: center;
              gap: 4px;
              color: #ff5e00;
              font-family: materialMedium;
              font-size: 16px;
              line-height: normal;
              background-color: transparent;
              border: none;
              cursor: pointer;
              opacity: 0;
              height: 0;
              transition:
                opacity 0.3s,
                height 0.3s;
            }
            &.is-active {
              .step-title {
                opacity: 1;
                .orange {
                  color: #ff5e00;
                }
              }
              .step-description {
                opacity: 1;
                height: auto;
              }
              .step-action {
                opacity: 1;
                height: auto;
              }
            }
          }
        }
      }
    }
    .image-section {
      display: flex;
      align-items: center;
      justify-content: center;

      .image-container {
        width: 316px;
        height: 485px;
        position: relative;
      }
    }
  }
`;

export default StepsSection;
